import { create } from 'zustand';
import { api } from '../services/api';

export interface ChatReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
}

export interface ChatMessage {
  id: string;
  family_id: string;
  sender_id: string;
  sender_display_name: string;
  sender_colour: string;
  content: string;
  media_url?: string;
  media_type?: string;
  reply_to_id?: string;
  edited_at?: string | null;
  deleted_at?: string | null;
  pinned_at?: string | null;
  poll_id?: string | null;
  created_at: string;
  reactions: ChatReaction[];
}

interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  hasMore: boolean;
  error: string | null;
  fetchMessages: (before?: string) => Promise<void>;
  sendMessage: (data: { content?: string; mediaUrl?: string; mediaType?: string; replyToId?: string; pollId?: string }) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
  togglePin: (messageId: string) => Promise<void>;
  handleWsMessage: (message: any) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isLoading: false,
  hasMore: true,
  error: null,

  fetchMessages: async (before?: string) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (before) params.append('before', before);
      const res = await api.get(`/chat?${params.toString()}`);
      // Backend already returns ASC order (it queries DESC then reverses)
      const fetched: ChatMessage[] = res.data.messages || [];
      set(state => ({
        messages: before ? [...fetched, ...state.messages] : fetched,
        hasMore: fetched.length === 50,
        isLoading: false,
      }));
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  sendMessage: async (data) => {
    try {
      const res = await api.post('/chat', data);
      const newMessage = res.data.message;
      set(state => {
        if (state.messages.some(m => m.id === newMessage.id)) return state;
        return { messages: [...state.messages, newMessage] };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  editMessage: async (messageId, content) => {
    try {
      const res = await api.put(`/chat/${messageId}`, { content });
      const updatedMessage = res.data.message;
      set(state => ({
        messages: state.messages.map(m => m.id === messageId ? updatedMessage : m),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteMessage: async (messageId) => {
    try {
      await api.delete(`/chat/${messageId}`);
      set(state => ({
        messages: state.messages.filter(m => m.id !== messageId),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  addReaction: async (messageId, emoji) => {
    try {
      const res = await api.post(`/chat/${messageId}/reactions`, { emoji });
      const reaction: ChatReaction = res.data.reaction;
      // Dedup: WebSocket CHAT_REACTION_ADDED may have already added this
      set(state => ({
        messages: state.messages.map(m => {
          if (m.id !== messageId) return m;
          const exists = m.reactions.find(r => r.id === reaction.id);
          if (exists) return m;
          return { ...m, reactions: [...m.reactions, reaction] };
        }),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  removeReaction: async (messageId, emoji) => {
    try {
      await api.delete(`/chat/${messageId}/reactions/${emoji}`);
      set(state => ({
        messages: state.messages.map(m =>
          m.id === messageId
            ? { ...m, reactions: m.reactions.filter(r => r.emoji !== emoji) }
            : m
        ),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  togglePin: async (messageId) => {
    try {
      const res = await api.put(`/chat/${messageId}/pin`);
      set(state => ({
        messages: state.messages.map(m => m.id === messageId ? { ...m, pinned_at: res.data.pinned_at } : m),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'CHAT_MESSAGE_PINNED':
        set(state => ({
          messages: state.messages.map(m => m.id === message.payload.messageId ? { ...m, pinned_at: message.payload.pinned_at } : m),
        }));
        break;

      case 'CHAT_MESSAGE':
        set(state => {
          const exists = state.messages.find(m => m.id === message.payload.id);
          if (exists) return state;
          return { messages: [...state.messages, message.payload] };
        });
        break;

      case 'CHAT_MESSAGE_EDITED':
        set(state => ({
          messages: state.messages.map(m =>
            m.id === message.payload.id ? { ...m, ...message.payload } : m
          ),
        }));
        break;

      case 'CHAT_MESSAGE_DELETED':
        set(state => ({
          messages: state.messages.filter(m => m.id !== message.payload.messageId),
        }));
        break;

      case 'CHAT_REACTION_ADDED':
        set(state => ({
          messages: state.messages.map(m => {
            if (m.id !== message.payload.messageId) return m;
            const alreadyExists = m.reactions.some(r => r.id === message.payload.reaction.id);
            if (alreadyExists) return m;
            return { ...m, reactions: [...m.reactions, message.payload.reaction] };
          }),
        }));
        break;

      case 'CHAT_REACTION_REMOVED':
        set(state => ({
          messages: state.messages.map(m =>
            m.id === message.payload.messageId
              ? { ...m, reactions: m.reactions.filter(r => !(r.user_id === message.payload.userId && r.emoji === message.payload.emoji)) }
              : m
          ),
        }));
        break;
    }
  },
}));
